import ActionMessages from '@components/ActionMessages';
import Wolfie from '@components/ai/Wolfie';
import WolfieDevPanel from '@components/ai/WolfieDevPanel';
import ProfileCompletionModal from '@components/modals/ProfileCompletionModal';
import Sidebar from '@components/Sidebar';
import { graphQlClient } from '@config/graphqlClient';
import UserGraphQL from '@graphql/user.queries';
import { getSelfWithOrganizationRequest } from '@models/user.model';
import { CssBaseline, styled } from '@mui/material';
import Box from '@mui/material/Box';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { setUserDetails, setUserOrganizations } from '@redux/reducers/user.reducer';
import { setOrganizationData } from '@redux/reducers/organization.reducer';
import { RootState } from '@redux/store';
import { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

const ContentContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})<{ collapsed?: boolean }>(({ theme, collapsed }) => ({
  marginLeft: collapsed ? 70 : 240,
  padding: theme.spacing(3),
}));

const PrintPreviewContentContainer = styled(Box)(() => ({
  marginLeft: 0,
  padding: 0,
  width: '100%',
}));

export default function Layout({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { permanentlyDisabled, devPanelEnabled } = useAppSelector((state: RootState) => state.wolfie);
  const { collapsedSidebar, firstName, lastName } = useAppSelector((state: RootState) => state.user);
  const enabledWolfie = process.env.REACT_APP_ENABLE_WOLFIE === 'true' && !permanentlyDisabled;
  const history = useHistory();

  // Check if we're on the print preview page
  const isPrintPreview = location.pathname.includes('/print');

  // Check if user profile is incomplete
  const [showProfileCompletion, setShowProfileCompletion] = useState(false);

  // WolfieHub position state - stored in localStorage
  const [wolfieOnBottom, setWolfieOnBottom] = useState(() => {
    const stored = localStorage.getItem('wolfieHubPosition');
    return stored === null ? true : stored === 'bottom';
  });

  const toggleWolfiePosition = () => {
    const newPosition = !wolfieOnBottom;
    setWolfieOnBottom(newPosition);
    localStorage.setItem('wolfieHubPosition', newPosition ? 'bottom' : 'top');
  };

  useEffect(() => {
    // Only show modal if user data has been loaded (not undefined) but is incomplete
    // This prevents flickering on initial load when firstName/lastName are undefined
    const userDataLoaded = firstName !== undefined && lastName !== undefined;
    const isProfileIncomplete = userDataLoaded && (!firstName?.trim() || !lastName?.trim());
    setShowProfileCompletion(isProfileIncomplete);
  }, [firstName, lastName]);

  const handleProfileCompletion = async () => {
    try {
      // Fetch the updated user data after profile completion
      const userData: getSelfWithOrganizationRequest = await graphQlClient.request(
        UserGraphQL.queries.getSelfWithOrganization,
      );
      const user = userData.getSelfWithOrganization.user;
      const role = userData.getSelfWithOrganization.role;
      const organization = userData.getSelfWithOrganization.organization;
      const organizations = userData.getSelfWithOrganization.organizations || [];

      // Update Redux state with fresh data
      if (organization) {
        dispatch(setOrganizationData(organization));
      }
      dispatch(setUserDetails({ ...user, role }));
      dispatch(setUserOrganizations(organizations));

      // Modal will close automatically when firstName/lastName are updated in Redux
      setShowProfileCompletion(false);
    } catch (error) {
      console.error('Failed to refresh user data after profile completion:', error);
      // Still close the modal and refresh the page as fallback
      setShowProfileCompletion(false);
      history.go(0);
    }
  };

  return (
    <Box display="flex">
      <ActionMessages />
      <CssBaseline />

      {/* Hide sidebar on print preview */}
      {!isPrintPreview && <Sidebar />}

      {/* Use appropriate container based on page type */}
      {isPrintPreview ? (
        <PrintPreviewContentContainer component="main" className="flex flex-grow bg-white min-h-screen">
          {children}
        </PrintPreviewContentContainer>
      ) : (
        <ContentContainer collapsed={collapsedSidebar} component="main" className="flex flex-grow bg-backgroundUI min-h-screen p-8 h-full">
          {children}
        </ContentContainer>
      )}

      {/* Hide Wolfie on print preview */}
      {!isPrintPreview && (
        <Box
          className={`fixed z-50 transition-all duration-300`}
          style={{
            [wolfieOnBottom ? 'bottom' : 'top']: '40px',
            right: devPanelEnabled ? '25%' : '30px',
          }}>
          {enabledWolfie && <Wolfie onTogglePosition={toggleWolfiePosition} isOnBottom={wolfieOnBottom} />}
        </Box>
      )}
      {!isPrintPreview && devPanelEnabled && <WolfieDevPanel />}

      {/* Profile Completion Modal - shows when user has incomplete profile */}
      <ProfileCompletionModal isOpen={showProfileCompletion} onComplete={handleProfileCompletion} />
    </Box>
  );
}
